import { ausrehabApi as baseApi } from '../baseApi';
import type {
	RequestAssetProps,
	PaymentSummaryProps,
	QuestionnaireAnswerResponse,
	GenerateTokenResponse,
	SubmitQuestionnaireBody,
} from '../../models';
import type {
	GetRequestsResult,
	PrebillingInvoiceResponse,
	GetRequestsResponseData,
	GetRequestsProps,
} from './apis.types';

const requestApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getRequests: builder.query<GetRequestsResult, GetRequestsProps | undefined>({
			query: ({ paginationModel, sortModel, filterModel, requestFilters } = {}) => ({
				url: 'requests',
				params: {
					paginationModel: JSON.stringify(paginationModel),
					sortModel: JSON.stringify(sortModel),
					filterModel: JSON.stringify(filterModel),
					requestFilters: JSON.stringify(requestFilters),
				},
			}),
			providesTags: ['Request'],
			transformResponse: (response: GetRequestsResult): GetRequestsResult => {
				return {
					...response,
					data: response.data
						.filter((res) => res.candidate)
						.map((res) => ({
							...res,
							candidateName: `${res.candidate.firstname} ${res.candidate.lastname}`,
						})),
				};
			},
		}),

		getRequestAssignment: builder.query<GetRequestsResult, GetRequestsProps | undefined>({
			query: ({ paginationModel, sortModel, filterModel, requestFilters } = {}) => ({
				url: 'requests/assignments',
				params: {
					paginationModel: JSON.stringify(paginationModel),
					sortModel: JSON.stringify(sortModel),
					filterModel: JSON.stringify(filterModel),
					requestFilters: JSON.stringify(requestFilters),
				},
			}),
			providesTags: ['Request'],
			transformResponse: (response: GetRequestsResult): GetRequestsResult => {
				return {
					...response,
					data: response.data
						.filter((res) => res.candidate)
						.map((res) => ({
							...res,
							candidateName: `${res.candidate.firstname} ${res.candidate.lastname}`,
						})),
				};
			},
		}),

		getRequest: builder.query<GetRequestsResponseData, string>({
			query: (id) => ({
				url: `requests/${id}`,
			}),
			providesTags: ['Request'],
		}),

		getRequestPaymentSummary: builder.query<PaymentSummaryProps, string>({
			query: (id) => ({
				url: `requests/${id}/paymentSummary`,
			}),
			providesTags: ['Request'],
		}),

		getRequestReports: builder.query<RequestAssetProps[], number>({
			query: (id) => ({
				url: `requests/${id}/reports`,
			}),
			providesTags: ['Request-Reports'],
		}),

		uploadRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reports } = payload;

				return {
					url: `requests/${requestId}/reports`,
					method: 'POST',
					body: {
						reports,
					},
				};
			},
			invalidatesTags: ['Request-Reports'],
		}),

		deleteRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reportName } = payload;

				return {
					url: `requests/${requestId}/reports`,
					method: 'DELETE',
					params: {
						report_name: reportName,
					},
				};
			},
			invalidatesTags: ['Request-Reports'],
		}),

		getRequestReport: builder.query<RequestAssetProps, { requestId: number; reportName: string; action?: string }>({
			query: (args) => {
				const { requestId, reportName, action } = args;
				return {
					url: `requests/${requestId}/reports/${reportName}`,
					method: 'HEAD',
					params: {
						action,
					},
				};
			},
			transformResponse: (response: RequestAssetProps, meta: { response: Response }) => {
				return {
					...response,
					uri: meta.response.headers.get('request-asset-uri') ?? '',
				};
			},
			providesTags: ['Request'],
		}),

		addRequest: builder.mutation({
			invalidatesTags: ['Request'],
			query: (payload) => {
				payload.id = undefined;

				return {
					url: 'requests',
					method: 'POST',
					body: payload,
				};
			},
		}),

		delRequest: builder.mutation({
			query: (id) => ({
				url: `requests/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Request'],
		}),

		// use updateRequestStatus for status update
		updateRequest: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}`,
					method: 'PUT',
					body: payload,
				};
			},
			invalidatesTags: ['Request'],
		}),

		updateRequestStatus: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/status`,
					method: 'PUT',
					body: {
						status: payload.status,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		updateRequestAssessmentDate: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/assessmentDates`,
					method: 'PUT',
					body: {
						assessmentDates: [payload.assessmentDate],
					},
				};
			},
			invalidatesTags: (_result, _error, payload) => [
				'Request',
				{ type: 'Questionnaire', id: `request-${payload.id}` },
			],
		}),

		assignRequest: builder.mutation<void, { requestIds: number[]; userId: number }>({
			query: ({ requestIds, userId }) => ({
				url: 'requests/assign',
				method: 'POST',
				body: { requestIds, userId },
			}),
			invalidatesTags: ['Request'],
		}),

		unassignRequest: builder.mutation<void, { requestIds: number[] }>({
			query: ({ requestIds }) => ({
				url: 'requests/assign',
				method: 'DELETE',
				body: { requestIds },
			}),
			invalidatesTags: ['Request'],
		}),

		// ── Request questionnaire ───────────────────────────────────────────────

		getRequestQuestionnaire: builder.query<QuestionnaireAnswerResponse, string>({
			query: (requestId) => ({ url: `requests/${requestId}/questionnaire` }),
			providesTags: (_result, _error, requestId) => [{ type: 'Questionnaire', id: `request-${requestId}` }],
		}),

		submitRequestQuestionnaire: builder.mutation<
			QuestionnaireAnswerResponse,
			{ requestId: string; body: SubmitQuestionnaireBody }
		>({
			query: ({ requestId, body }) => ({
				url: `requests/${requestId}/questionnaire`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['Questionnaire'],
		}),

		generateQuestionnaireToken: builder.mutation<GenerateTokenResponse, string>({
			query: (requestId) => ({
				url: `requests/${requestId}/questionnaire/token`,
				method: 'POST',
			}),
			invalidatesTags: ['Questionnaire'],
		}),

		getRequestQuestionnaireForms: builder.query<RequestAssetProps[], string>({
			query: (requestId) => ({ url: `requests/${requestId}/questionnaire/forms` }),
			providesTags: (_result, _error, requestId) => [{ type: 'Questionnaire-Form', id: `request-${requestId}` }],
		}),

		uploadRequestQuestionnaireForm: builder.mutation({
			query: (payload) => {
				const { requestId, forms } = payload;

				return {
					url: `requests/${requestId}/questionnaire/forms`,
					method: 'POST',
					body: {
						forms,
					},
				};
			},
			invalidatesTags: (_result, _error, payload) => [
				{ type: 'Questionnaire-Form', id: `request-${payload.requestId}` },
			],
		}),

		deleteRequestQuestionnaireForm: builder.mutation({
			query: (payload) => {
				const { requestId, formName } = payload;

				return {
					url: `requests/${requestId}/questionnaire/forms`,
					method: 'DELETE',
					params: {
						form_name: formName,
					},
				};
			},
			invalidatesTags: (_result, _error, payload) => [
				{ type: 'Questionnaire-Form', id: `request-${payload.requestId}` },
			],
		}),

		getRequestQuestionnaireForm: builder.query<
			RequestAssetProps,
			{ requestId: number; formName: string; action?: string }
		>({
			query: (args) => {
				const { requestId, formName, action } = args;
				return {
					url: `requests/${requestId}/questionnaire/forms/${formName}`,
					method: 'HEAD',
					params: {
						action,
					},
				};
			},
			transformResponse: (response: RequestAssetProps, meta: { response: Response }) => {
				return {
					...response,
					uri: meta.response.headers.get('request-asset-uri') ?? '',
				};
			},
		}),

		payRequest: builder.mutation({
			query: (payload) => {
				const { requestId, items } = payload;

				return {
					url: 'payments',
					method: 'POST',
					body: {
						requestId,
						items,
					},
				};
			},
		}),

		generatePrebillingInvoice: builder.mutation<PrebillingInvoiceResponse, number | string>({
			query: (id) => ({
				url: `requests/${id}/invoice`,
				method: 'POST',
			}),
			invalidatesTags: ['Request'],
		}),

		cancelRequest: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/cancel`,
					method: 'DELETE',
				};
			},
			invalidatesTags: ['Request'],
		}),

		setRequestAdminNote: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/adminNote`,
					method: 'PUT',
					body: {
						adminNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		setRequestAccountNote: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/accountNote`,
					method: 'PUT',
					body: {
						accountNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),

		updateRequestAttendanceStatus: builder.mutation({
			query: (payload) => {
				return {
					url: `requests/${payload.id}/attendanceStatus`,
					method: 'PUT',
					body: {
						attendanceStatus: payload.attendanceStatus,
					},
				};
			},
			invalidatesTags: ['Request'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetRequestQuery,
	useGetRequestsQuery,
	useGetRequestReportsQuery,
	useGetRequestPaymentSummaryQuery,
	useGetRequestAssignmentQuery,

	// mutation
	useDelRequestMutation,
	useUpdateRequestMutation,
	useUpdateRequestStatusMutation,
	useUpdateRequestAssessmentDateMutation,
	useAssignRequestMutation,
	useUnassignRequestMutation,
	useAddRequestMutation,
	usePayRequestMutation,
	useGeneratePrebillingInvoiceMutation,
	useCancelRequestMutation,
	useUploadRequestReportMutation,
	useDeleteRequestReportMutation,
	useLazyGetRequestReportQuery,
	useLazyGetRequestQuery,
	useSetRequestAdminNoteMutation,
	useSetRequestAccountNoteMutation,
	useUpdateRequestAttendanceStatusMutation,
	useGetRequestQuestionnaireQuery,
	useSubmitRequestQuestionnaireMutation,
	useGenerateQuestionnaireTokenMutation,
	useGetRequestQuestionnaireFormsQuery,
	useUploadRequestQuestionnaireFormMutation,
	useDeleteRequestQuestionnaireFormMutation,
	useLazyGetRequestQuestionnaireFormQuery,
} = requestApi;

export const getRequests = requestApi.endpoints.getRequests;
